/* eslint-disable no-param-reassign */
import { createAsyncThunk } from '@reduxjs/toolkit';

import getThumbnailUrls from '../../helpers/getThumbnailUrls';
import { setThumbnailUrls, extendAssets } from '../helpers/assets';
import { setListingVariantAssets, setMasterVariantAssets } from './helpers/entityAdapter';
import {
  setListingVariantAssetsFetching,
  setMasterVariantAssetsFetching,
} from './helpers/uiAdapter';
import { shouldSuppressMediaErrorToast } from './helpers/showChannelProductGoneToast';

const mapAssetsWithPermissions = (assets) => assets.map((asset) => ({
  ...asset,
  permissions: Object
    .keys(asset.permissions || {})
    .reduce((negativePermissions, key) => ({ ...negativePermissions, [key]: false }), {}),
}));

export const fetchVariantAssets = createAsyncThunk(
  'products/fetchVariantAssets',
  async ({
    variantId,
    productId,
    channelId,
    masterVariantId,
  }, {
    extra: { Logger, Toast, sdk },
    getState,
  }) => {
    try {
      const { team } = getState().user;
      const rootCollectionId = getState().collections?.collections?.my?._id;

      const { data } = await sdk.assets.search({
        tagId: rootCollectionId,
        from: 0,
        size: 250,
        productVariantId: variantId,
        sort: {
          type: 'custom',
        },
      });

      const assets = data.images?.length ? extendAssets(data.images) : [];
      const assetsWithThumbs = assets.length
        ? setThumbnailUrls(
          assets,
          await getThumbnailUrls(assets.map(({ _id }) => _id)),
          team?.storageType === 's3',
        )
        : [];

      return {
        variantId,
        productId,
        channelId: channelId || null,
        masterVariantId: masterVariantId || null,
        storageKey: channelId ? masterVariantId : variantId,
        assets: mapAssetsWithPermissions(assetsWithThumbs),
      };
    } catch (error) {
      Logger.log('UI', 'ToastProductsNotLoaded');
      if (!shouldSuppressMediaErrorToast()) {
        Toast(
          'Variant media not loaded. Refresh and try again',
          { autoClose: false, type: 'error' },
        );
      }
      Logger.error(new Error('Error variant assets fetching'), { error }, [
        'FetchVariantAssetsFailed',
        { errorMessage: 'Variant assets can not be fetched from backend.' },
      ]);
      throw error;
    }
  },
);

export const reducer = (builder) => {
  builder.addCase(
    fetchVariantAssets.pending,
    (state, { meta }) => {
      const {
        productId,
        variantId,
        channelId,
        masterVariantId,
      } = meta.arg;
      const storageKey = channelId ? masterVariantId : variantId;

      if (channelId) {
        setListingVariantAssetsFetching(state, variantId, true);
        return;
      }

      state.items.forEach((product) => {
        if (product._id !== productId) return;

        product.fetchingVariantAssetsById = {
          ...(product.fetchingVariantAssetsById || {}),
          [storageKey]: true,
        };
        setMasterVariantAssetsFetching(state, storageKey, true);
      });
    },
  ).addCase(
    fetchVariantAssets.fulfilled,
    (state, { payload }) => {
      const {
        productId,
        channelId,
        storageKey,
        variantId,
        assets,
      } = payload;

      if (channelId) {
        setListingVariantAssets(state, variantId, assets);
        setListingVariantAssetsFetching(state, variantId, false);
        return;
      }

      state.items.forEach((product) => {
        if (product._id !== productId) return;

        if (!product.variantAssetsById) product.variantAssetsById = {};
        product.variantAssetsById[storageKey] = assets;
        if (product.fetchingVariantAssetsById) {
          delete product.fetchingVariantAssetsById[storageKey];
        }
        setMasterVariantAssets(state, storageKey, assets);
        setMasterVariantAssetsFetching(state, storageKey, false);
      });
    },
  ).addCase(
    fetchVariantAssets.rejected,
    (state, { error, meta }) => {
      const {
        productId,
        variantId,
        channelId,
        masterVariantId,
      } = meta.arg;
      const storageKey = channelId ? masterVariantId : variantId;

      if (channelId) {
        setListingVariantAssetsFetching(state, variantId, false);
      } else {
        state.items.forEach((product) => {
          if (product._id !== productId) return;

          if (product.fetchingVariantAssetsById) {
            delete product.fetchingVariantAssetsById[storageKey];
          }
          setMasterVariantAssetsFetching(state, storageKey, false);
        });
      }
      state.error = error;
    },
  );
};
